import React, { useState, useMemo, useEffect } from 'react';
import { clsx } from 'clsx';
import { format, isPast } from 'date-fns';
import {
  PlayIcon,
  PauseIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  SparklesIcon,
  RocketLaunchIcon,
  TrophyIcon,
  FireIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { Task, TaskStatus, TaskType, TaskPriority } from '../../../types/task.types';
import { Button, Badge, Avatar } from '../../ui';
import { TaskProgressRing, ConfettiCelebration } from '../components';

interface FocusModeViewProps {
  tasks: Task[];
  onTaskComplete: (taskId: number) => void;
  onTaskClick: (task: Task) => void;
}

const typeIcons: Record<TaskType, React.ReactNode> = {
  CALL: <PhoneIcon className="h-8 w-8" />,
  EMAIL: <EnvelopeIcon className="h-8 w-8" />,
  MEETING: <UserGroupIcon className="h-8 w-8" />,
  TODO: <CheckCircleSolidIcon className="h-8 w-8" />,
  FOLLOW_UP: <ClockIcon className="h-8 w-8" />,
  CUSTOM: <SparklesIcon className="h-8 w-8" />,
};

const priorityColors: Record<TaskPriority, { bg: string; text: string; ring: string }> = {
  LOW: { bg: 'bg-gray-100', text: 'text-gray-600', ring: 'ring-gray-300' },
  NORMAL: { bg: 'bg-blue-100', text: 'text-blue-600', ring: 'ring-blue-300' },
  MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-700', ring: 'ring-yellow-300' },
  HIGH: { bg: 'bg-orange-100', text: 'text-orange-600', ring: 'ring-orange-300' },
  URGENT: { bg: 'bg-red-100', text: 'text-red-600', ring: 'ring-red-300' },
};

const motivationalMessages = [
  "You're on fire! 🔥",
  "Keep crushing it! 💪",
  "Unstoppable! 🚀",
  "Amazing progress! ⭐",
  "You're a legend! 🏆",
  "Productivity champion! 🎯",
];

const FocusModeView: React.FC<FocusModeViewProps> = ({
  tasks,
  onTaskComplete,
  onTaskClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  // Sort tasks by priority and due date
  const sortedTasks = useMemo(() => {
    const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, NORMAL: 3, LOW: 4 };
    return [...tasks]
      .filter(t => t.status !== TaskStatus.COMPLETED)
      .sort((a, b) => {
        const aPriority = priorityOrder[a.priority] || 3;
        const bPriority = priorityOrder[b.priority] || 3;
        if (aPriority !== bPriority) return aPriority - bPriority;
        if (a.dueDateTime && b.dueDateTime) {
          return new Date(a.dueDateTime).getTime() - new Date(b.dueDateTime).getTime();
        }
        if (a.dueDateTime) return -1;
        if (b.dueDateTime) return 1;
        return 0;
      });
  }, [tasks]);

  const currentTask = sortedTasks[currentIndex];
  const totalTasks = sortedTasks.length;

  const goToNext = () => {
    if (currentIndex < totalTasks - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    if (!currentTask) return;
    
    onTaskComplete(currentTask.id);
    setCompletedCount(prev => prev + 1);
    setShowCelebration(true);

    // Auto-advance after a short delay
    setTimeout(() => {
      if (currentIndex < totalTasks - 1) {
        goToNext();
      }
    }, 500);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Enter' && currentTask) handleComplete();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, currentTask]);

  if (totalTasks === 0) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <TrophyIcon className="h-24 w-24 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">All Done! 🎉</h2>
          <p className="text-gray-500">You've completed all your tasks. Time for a well-deserved break!</p>
        </div>
      </div>
    );
  }

  const priorityStyle = currentTask ? priorityColors[currentTask.priority] : priorityColors.NORMAL;

  return (
    <div className="min-h-[500px] relative">
      <ConfettiCelebration
        isActive={showCelebration}
        message={motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)]}
        onComplete={() => setShowCelebration(false)}
      />

      {/* Pipeline Progress */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <BoltIcon className="h-6 w-6 text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-900">Focus Pipeline</h2>
          </div>
          <div className="flex items-center gap-4">
            {completedCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full">
                <FireIcon className="h-4 w-4" />
                <span className="text-sm font-semibold">{completedCount} done!</span>
              </div>
            )}
            <TaskProgressRing completed={currentIndex} total={totalTasks} size="sm" />
          </div>
        </div>

        {/* Pipeline visual */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {sortedTasks.map((task, index) => {
            const isActive = index === currentIndex;
            const isPassed = index < currentIndex;
            const taskPriority = priorityColors[task.priority];

            return (
              <React.Fragment key={task.id}>
                <button
                  onClick={() => setCurrentIndex(index)}
                  className={clsx(
                    'flex-shrink-0 h-3 rounded-full transition-all duration-300 cursor-pointer',
                    isActive ? 'w-12 ring-2 ring-offset-2' : 'w-3 hover:w-6',
                    isActive && taskPriority.ring,
                    isPassed ? 'bg-green-500' : taskPriority.bg.replace('bg-', 'bg-opacity-100 bg-')
                  )}
                  title={task.title}
                />
                {index < sortedTasks.length - 1 && (
                  <div className={clsx(
                    'flex-shrink-0 h-0.5 w-2',
                    isPassed ? 'bg-green-400' : 'bg-gray-200'
                  )} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Current Task Spotlight */}
      {currentTask && (
        <div className={clsx(
          'bg-white rounded-3xl shadow-xl border-2 overflow-hidden transition-all duration-300',
          priorityStyle.ring.replace('ring-', 'border-')
        )}>
          {/* Task Header */}
          <div className={clsx('p-6', priorityStyle.bg)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={clsx(
                  'p-4 rounded-2xl bg-white shadow-lg',
                  priorityStyle.text
                )}>
                  {typeIcons[currentTask.type]}
                </div>
                <div>
                  <Badge size="sm" variant={currentTask.priority === 'URGENT' ? 'error' : 'neutral'}>
                    {currentTask.priority} Priority
                  </Badge>
                  <p className="text-sm text-gray-500 mt-1">
                    Task {currentIndex + 1} of {totalTasks}
                  </p>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevious}
                  disabled={currentIndex === 0}
                  className={clsx(
                    'p-2 rounded-full transition-colors',
                    currentIndex === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-white hover:shadow'
                  )}
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={currentIndex >= totalTasks - 1}
                  className={clsx(
                    'p-2 rounded-full transition-colors',
                    currentIndex >= totalTasks - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-white hover:shadow'
                  )}
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Task Content */}
          <div className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{currentTask.title}</h1>

            {currentTask.description && (
              <p className="text-lg text-gray-600 mb-6">{currentTask.description}</p>
            )}

            {/* Due date */}
            {currentTask.dueDateTime && (
              <div className={clsx(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6',
                isPast(new Date(currentTask.dueDateTime))
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
              )}>
                <ClockIcon className="h-5 w-5" />
                <span className="font-medium">
                  Due: {format(new Date(currentTask.dueDateTime), 'EEEE, MMM d, yyyy h:mm a')}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-4 mt-8">
              <Button
                onClick={handleComplete}
                variant="primary"
                size="lg"
                className="flex-1 py-4 text-lg"
                leftIcon={<CheckIcon className="h-6 w-6" />}
              >
                Complete Task
              </Button>
              <Button
                onClick={() => onTaskClick(currentTask)}
                variant="outline"
                size="lg"
                className="py-4"
              >
                View Details
              </Button>
            </div>

            {/* Keyboard hint */}
            <p className="text-center text-sm text-gray-400 mt-6">
              Use ← → arrow keys to navigate • Press Enter to complete
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusModeView;

